

<template>
  <div
      v-if="showLoader"
      class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50"
  >
    <div
        class="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-yellow-500"
    ></div>
  </div>
  <div v-else>
    <div class="flex flex-wrap items-center justify-between pb-4 py-1">
      <div class="flex items-center space-x-4">
        <div class="relative">
          <button
              id="dropdownRadioButton"
              class="inline-flex items-center text-gray-400 bg-white border border-gray-300 rounded-md px-6 py-2"
              type="button"
              @click="toggleDropdown"
          >
            <svg
                class="w-6 h-6"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                fill="none"
                viewBox="0 0 24 24"
            >
              <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-width="2"
                  d="M18.796 4H5.204a1 1 0 0 0-.753 1.659l5.302 6.058a1 1 0 0 1 .247.659v4.874a.5.5 0 0 0 .2.4l3 2.25a.5.5 0 0 0 .8-.4v-7.124a1 1 0 0 1 .247-.659l5.302-6.059c.566-.646.106-1.658-.753-1.658Z"
              />
            </svg>
            Filters
            <svg
                class="w-2.5 h-2.5 ms-2.5"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 10 6"
            >
              <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="m1 1 4 4 4-4"
              />
            </svg>
          </button>
          <!-- start dropdown filter -->
          <template v-if="currentTab === 'patient-queue'">
            <div
                v-show="isDropdownOpen"
                id="filterDropdown"
                class="absolute z-10 mt-2 w-80 p-4 bg-white border border-gray-300 rounded-lg shadow-lg"
            >
              <div class="flex">
                <button
                    type="button"
                    class="ml-auto -mx-1.5 -my-1.5 p-1.5 inline-flex text-gray-500 items-center justify-end h-8 w-8 hover:text-custom-yellow"
                    @click="closeDropdown"
                    aria-label="Close"
                >
                  <span class="sr-only">Dismiss</span>
                  <svg
                      class="w-3 h-3"
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 14 14"
                  >
                    <path
                        stroke="currentColor"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                    />
                  </svg>
                </button>
              </div>
              <form class="max-w-sm mx-auto" @submit.prevent="applyFilters">
                <div class="mb-4">
                  <label
                      for="priority"
                      class="block mb-2 text-sm font-medium text-gray-900"
                  >Status</label
                  >
                  <select
                      id="priority"
                      v-model="filters.priority"
                      class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                  >
                    <option value="">All</option>
                    <option value="Unpaid">Unpaid</option>
                    <option value="Paid">Paid</option>
                  </select>
                </div>
                <div class="mb-4">
                  <label
                      for="dateRequested"
                      class="block mb-2 text-sm font-medium text-gray-900"
                  >Date Processed</label
                  >
                  <input
                      type="date"
                      id="dateRequested"
                      v-model="filters.dateRequested"
                      class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                  />
                </div>
                <div class="flex justify-end space-x-2">
                  <button
                      type="button"
                      @click="cancelFilters"
                      class="px-4 py-2 text-sm font-medium text-gray-900 bg-white border border-gray-300 rounded-lg hover:bg-gray-100"
                  >
                    Cancel
                  </button>
                  <button
                      type="submit"
                      class="px-4 py-2 text-sm font-medium text-white bg-custom-yellow rounded-lg hover:bg-custom-yellow-dark"
                  >
                    Filter
                  </button>
                </div>
              </form>
            </div>
          </template>
        </div>

        <div class="relative flex">
          <input
              type="text"
              v-model="searchQuery"
              id="table-search"
              class="px-4 py-2 text-gray-100 border border-gray-300 rounded-sm dark:border-gray-300 dark:placeholder-gray-400"
              placeholder="Search"
          />
          <button
              type="submit"
              class="bg-custom-yellow text-white rounded-r px-2 md:px-3 py-0 md:py-1"
          >
            <svg
                class="w-6 h-6 text-gray-100 dark:text-white"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                fill="none"
                viewBox="0 0 24 24"
            >
              <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-width="2"
                  d="m21 21-3.5-3.5M17 10a7 7 0 1 1-14 0 7 7 0 0 1 14 0Z"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>

    <div class="tabs flex border-b mb-4">
      <button
          class="px-4 py-2 -mb-px font-semibold border-b-2"
          :class="{
          'border-custom-yellow text-custom-blue':
            currentTab === 'patient-queue',
          'text-gray-500': currentTab !== 'patient-queue',
        }"
          @click="currentTab = 'patient-queue'"
      >
        System Bills
      </button>
    </div>

    <component
        :is="currentTabComponent"
        ref="currentTabComponent"
        :search-query="searchQuery"
    ></component>
  </div>


</template>


<script setup>
import {computed, ref} from 'vue';
import SystemBillingListTab from "@/views/tabs/system_billing_tabs/SystemBillingListTab.vue";

  const showLoader = ref(false);
  const currentTab = ref('SystemBillingListTab');
  const isDropdownOpen =  ref(false);
  const filters = ref({
    dateRequested: "",
  });

  const currentTabComponent = computed(() => {
    switch (currentTab.value) {
      case "systemBillingListTab":
        return SystemBillingListTab;
      default:
        return SystemBillingListTab;
    }
  });

  const applyFilters = () => {
    // Implement filter logic here
    closeDropdown();
  };

  const cancelFilters = () => {
    filters.value = {
      dateRequested: "",
      priority: "",
      status: "",
    };
    closeDropdown();
  };

  const toggleDropdown = () => {
    isDropdownOpen.value = !isDropdownOpen.value;
  };

  const closeDropdown = () => {
    isDropdownOpen.value = false;
  };

</script>

<style scoped>

</style>